/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1686787200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Jun 15 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "drift",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Drift",
          "depth": 0,
          "id": "connect-to-drift"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing accounts",
          "depth": 1,
          "id": "syncing-accounts"
        }, {
          "value": "Syncing contacts",
          "depth": 1,
          "id": "syncing-contacts"
        }, {
          "value": "Syncing users",
          "depth": 1,
          "id": "syncing-users"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Accounts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Drift accounts"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://devdocs.drift.com/docs/account-model",
                children: "Accounts docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Drift contacts"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://devdocs.drift.com/docs/contact-model",
                children: "Contacts docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Drift users"
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://devdocs.drift.com/docs/user-model",
                children: "Users docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-drift",
        children: "Connect to Drift"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Drift"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Drift"
        }), " using OAuth."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Drift"
        }), " and log into your Drift account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Drift destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Drift destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-accounts",
        children: "Syncing accounts"
      }), "\n", _jsx(_components.p, {
        children: "Accounts can be used for ABM (account based marketing) in Drift, and can be tied to the display conditions of particular playbooks-enabling account specific marketing strategies."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to accounts in Drift, you need to select a model column and corresponding Drift field. You can match on any of the following Drift fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Domain"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Account ID"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Drift automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync account fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom account fields.\nIf you send data for a custom field that doesn't exist, Hightouch adds the field.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Delete"
          }), " is only available on ", _jsx(_components.strong, {
            children: "Upsert"
          }), " mode and ", _jsx(_components.strong, {
            children: "Clear"
          }), " is available for\n", _jsx(_components.strong, {
            children: "Upsert"
          }), " and ", _jsx(_components.strong, {
            children: "Update"
          }), " mode."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the account in Drift with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the account in Drift"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced accounts from Drift"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts",
        children: "Syncing contacts"
      }), "\n", _jsx(_components.p, {
        children: "Contacts are your site visitors who interact with Drift. Contacts may provide you with their email address, or their information may have been updated in the past 90 days. Contacts belong to an account, and may have conversations, composed of messages, with your users."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to contacts in Drift on any column in your model and any field in Drift. Ensure the data types of the model column and Drift field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Drift automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync contact fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom contact fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Delete"
          }), " is only available on ", _jsx(_components.strong, {
            children: "Upsert"
          }), " mode and ", _jsx(_components.strong, {
            children: "Clear"
          }), " is available for\n", _jsx(_components.strong, {
            children: "Upsert"
          }), " and ", _jsx(_components.strong, {
            children: "Update"
          }), " mode."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the contact in Drift with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the contact in Drift"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced contacts from Drift"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-users",
        children: "Syncing users"
      }), "\n", _jsx(_components.p, {
        children: "Drift users are internal agents to your organization or account-while site visitors or contacts are those that are interacting from your website.\nHightouch syncs can only update existing users."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to users in Drift on any column in your model and any field in Drift. Ensure the data types of the model column and Drift field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync user fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default user fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-2",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the user in Drift with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the user in Drift"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
